import React from "react";
import { FilePlus, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { formatDate, formatCurrency } from "../../lib/contractUtils";
import { ExtractObligationsButton } from "./ExtractObligationsButton";
import { statusColor } from "../ui/status-colors";

const typeColors = {
  amendment: "bg-blue-100 text-blue-700",
  extension: "bg-emerald-100 text-emerald-700",
  variation: "bg-amber-100 text-amber-700",
  supplement: "bg-purple-100 text-purple-700",
};

const addendumStatusColors = {
  draft: "bg-slate-100 text-slate-700",
  under_review: "bg-amber-100 text-amber-700",
  pending_approval: "bg-cyan-100 text-cyan-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  revision: "bg-orange-100 text-orange-700",
  executed: "bg-cyan-100 text-cyan-700",
};

export function AddendaTab({ addenda, addendumWorkflows, user, contractId, onCreateAddendum, onPreviewAddendum, onSubmitForReview, onWorkflowAction }) {
  if (addenda.length === 0) {
    return (
      <div className="text-center py-20 text-slate-400">
        <FilePlus size={48} className="mx-auto mb-4 opacity-50" />
        <p>No addenda yet</p>
        <Button variant="link" className="text-cyan-600 mt-2" onClick={onCreateAddendum}>Create an addendum</Button>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-4">
        {addenda.map((a) => {
          const wf = addendumWorkflows[a.id];
          const canReviewAddendum = wf?.stage === "review" && wf?.review_steps?.some((s) => s.user_id === user?.id && s.status === "pending");
          const canApproveAddendum = wf?.stage === "approval" && wf?.approval_steps?.some((s) => s.user_id === user?.id && s.status === "pending");
          const canResubmit = wf?.stage === "revision" && wf?.created_by === user?.id;

          return (
            <div key={a.id} className="p-4 border rounded-sm" data-testid={`addendum-${a.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-sm text-xs ${typeColors[a.addendum_type] || "bg-slate-100"}`}>{a.addendum_type}</Badge>
                  <span className="font-medium">{a.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-sm text-xs ${addendumStatusColors[a.status] || "bg-slate-100"}`}>
                    {(a.status || "").replace(/_/g, " ")}
                  </Badge>
                  {a.status === "draft" && a.created_by === user?.id && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm gap-1" data-testid={`preview-addendum-${a.id}`} onClick={() => onPreviewAddendum(a)}>
                        <Eye size={12} />View Draft
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" data-testid={`submit-addendum-review-${a.id}`} onClick={() => onSubmitForReview(a.id)}>
                        Submit for Review
                      </Button>
                    </>
                  )}
                  {canReviewAddendum && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm gap-1" data-testid={`preview-addendum-${a.id}`} onClick={() => onPreviewAddendum(a)}>
                        <Eye size={12} />View
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm text-emerald-600 border-emerald-300" data-testid={`approve-review-addendum-${a.id}`} onClick={() => onWorkflowAction(wf.id, "review-approve")}>
                        Approve Review
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm text-orange-600 border-orange-300" data-testid={`send-back-addendum-${a.id}`} onClick={() => onWorkflowAction(wf.id, "send-back")}>
                        Send Back
                      </Button>
                    </>
                  )}
                  {canApproveAddendum && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm gap-1" data-testid={`preview-addendum-${a.id}`} onClick={() => onPreviewAddendum(a)}>
                        <Eye size={12} />View
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm text-emerald-600 border-emerald-300" data-testid={`approve-addendum-${a.id}`} onClick={() => onWorkflowAction(wf.id, "approve")}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm text-red-600 border-red-300" data-testid={`reject-addendum-${a.id}`} onClick={() => onWorkflowAction(wf.id, "reject", "Rejected by approver")}>
                        Reject
                      </Button>
                    </>
                  )}
                  {canResubmit && (
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" data-testid={`resubmit-addendum-${a.id}`} onClick={() => onWorkflowAction(wf.id, "resubmit")}>
                      Resubmit
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">{a.description}</p>
              {a.changes_summary && <p className="text-sm text-slate-500 mt-1"><strong>Changes:</strong> {a.changes_summary}</p>}
              <div className="flex gap-4 mt-2 text-xs text-slate-400">
                {a.effective_date && <span>Effective: {formatDate(a.effective_date)}</span>}
                {a.new_expiration_date && <span>New Expiry: {formatDate(a.new_expiration_date)}</span>}
                {a.new_value && <span>New Value: {formatCurrency(a.new_value)}</span>}
                <span>By: {a.created_by_name}</span>
                <span>{formatDate(a.created_at)}</span>
              </div>
              {wf && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="font-mono uppercase text-slate-400">Workflow:</span>
                    <div className="flex items-center gap-1">
                      {wf.review_steps?.map((s, i) => (
                        <span key={s.user_id || i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs ${statusColor(s.status)}`}>
                          {s.user_name} ({s.role})
                        </span>
                      ))}
                      {wf.approval_steps?.map((s, i) => (
                        <span key={s.user_id || i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs ${statusColor(s.status)}`}>
                          {s.user_name} ({s.role})
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {(a.status === "approved" || a.status === "executed") && contractId && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <ExtractObligationsButton
                    eventType="addendum"
                    eventId={a.id}
                    contractId={contractId}
                    label="AI Extract Obligations"
                    className="h-7 text-xs"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
